import './app-settings.scss';

import React, {
  FunctionComponent,
  useCallback,
  useReducer,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Alert from '@justgiving/alert';
import Loading from '@justgiving/loading';
import Modal from '@justgiving/modal';
import config from '../../config';
import disconnectStravaThunk from '../../actions/app-settings/disconnectStrava.thunk';
import { useTranslation } from 'react-i18next';

type Props = {
  className?: string;
};

type SelectedState = {
  IsStravaConnected: boolean;
};

const StravaSettings: FunctionComponent<Props> = ({ className = '' }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { IsStravaConnected } = useSelector<State, SelectedState>(state => ({
    IsStravaConnected: state.app.fitness.data.isConnected,
  }));

  const initialState = {
    isLoading: false,
    isError: false,
    isSuccess: false,
  };
  const reducer = (state: any, action: { type: string }) => {
    switch (action.type) {
      case 'DISCONNECTING':
        return { isSuccess: false, isLoading: true, isError: false };
      case 'DISCONNECTING_SUCCESS':
        return { isError: false, isLoading: false, isSuccess: true };
      case 'AFTER_DISCONNECTING_SUCCESS':
        return { isError: false, isLoading: false, isSuccess: false };
      case 'DISCONNECTING_FAILURE':
        return {
          isLoading: false,
          isSuccess: false,
          isError: true,
        };
      default:
        return state;
    }
  };

  const [{ isLoading, isError, isSuccess }, dispatchState] = useReducer(
    reducer,
    initialState,
  );

  const openModal = useCallback(() => setIsModalOpen(true), [setIsModalOpen]);
  const closeModal = useCallback(() => setIsModalOpen(false), [setIsModalOpen]);

  const disconnectStrava = useCallback(async () => {
    try {
      dispatchState({ type: 'DISCONNECTING' });
      await dispatch(disconnectStravaThunk());
      dispatchState({ type: 'DISCONNECTING_SUCCESS' });
    } catch {
      dispatchState({ type: 'DISCONNECTING_FAILURE' });
    }
  }, [dispatch, dispatchState]);

  if (isSuccess) {
    return (
      <Alert
        autoClose={5000}
        onClose={() => dispatchState({ type: 'AFTER_DISCONNECTING_SUCCESS' })}
        displayType="inline"
        type="success"
        className="jg-space-mvlg"
      >
        {t('StravaSettings.success')}
      </Alert>
    );
  }

  if (!IsStravaConnected) return null;

  return (
    <>
      <section className={`app-settings ${className}`}>
        <h2 className="jg-h4">Strava</h2>
        <div className="app-settings__row">
          <div className="app-settings__logo">
            <img
              src={`${config.publicPath}Strava.svg`}
              width="48"
              height="48"
              alt="Strava logo"
            />
          </div>
          <div className="app-settings__content">
            {t('StravaSettings.description')}
            <button
              className="app-settings__button jg-text--link"
              onClick={openModal}
              type="button"
            >
              {t('StravaSettings.ButtonLabel')}
            </button>
          </div>
        </div>
      </section>
      <Modal
        className="strava-wrapper"
        classNames="jg-space-pxs"
        isOpen={isModalOpen}
        onRequestClose={closeModal}
      >
        <div className="app-settings__modal">
          <h2 className="jg-text--center app-settings__modal__title">
            {t('StravaSettings.ModalHeading')}
          </h2>

          <p className="jg-text jg-space-mvlg app-settings__modal__description">
            {t('StravaSettings.ModalDescription-pt_1')}
          </p>
          <p className="jg-text jg-space-mvlg app-settings__modal__description">
            {t('StravaSettings.ModalDescription-pt_2')}
          </p>
          <p className="jg-text jg-space-mvlg app-settings__modal__description">
            {t('StravaSettings.ModalDescription-pt_3')}
          </p>

          <div className="app-settings__modal__buttons">
            {isError && (
              <Alert
                displayType="inline"
                type="warning"
                className="jg-space-mbmd"
              >
                {t('StravaSettings.failure')}
              </Alert>
            )}
            <button
              onClick={disconnectStrava}
              className="jg-btn jg-btn--brand jg-space-mbsm"
              disabled={isLoading}
            >
              {isLoading ? (
                <Loading size="small" colour="white" />
              ) : (
                t('StravaSettings.ModalButtonLabel')
              )}
            </button>
            <button onClick={closeModal} className="jg-btn jg-btn--ghost">
              {t('StravaSettings.ModalButtonNotNow')}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default StravaSettings;
